import React from 'react';
import Navigation from '../tulote/Navigation';
import Footer from '../tulote/Footer';
import { FileJson, RefreshCw, Users, Camera, Glasses, BookOpen, PlayCircle, Plug } from 'lucide-react';

const DocumentacionPage = () => {
  const documentacionItems = [
    {
      title: 'Formato GeoJSON',
      description: 'Aprende sobre el formato GeoJSON y como estructurar tus datos geoespaciales para importarlos al sistema.',
      icon: FileJson,
      link: '/documentacion-geojson',
      color: 'bg-blue-500',
      tags: ['Lotes', 'Areas', 'Lineas']
    },
    {
      title: 'Convertir a GeoJSON',
      description: 'Herramienta para convertir archivos Shapefile (.shp), KML, y otros formatos a GeoJSON.',
      icon: RefreshCw,
      link: '/convertir-geojson',
      color: 'bg-green-500',
      tags: ['SHP', 'KML', 'Convertir']
    },
    {
      title: 'Integración con tu CRM',
      description: 'Conecta tu propio CRM con LotesPro. Actualiza el estado de los lotes vía API y el mapa se sincroniza solo.',
      icon: Plug,
      link: '/integracion-crm',
      color: 'bg-brand-500',
      tags: ['API', 'CRM', 'Webhook']
    },
    {
      title: 'Importar Clientes',
      description: 'Importa tu base de clientes desde el Dashboard usando archivos Excel o CSV. Incluye campos como nombre, telefono, email y mas.',
      icon: Users,
      link: 'https://dashboard.lotes.info/clientes/importar',
      color: 'bg-purple-500',
      tags: ['Excel', 'CSV', 'Dashboard']
    },
    {
      title: 'Video Tutoriales',
      description: 'Mira nuestros tutoriales en YouTube para aprender como funciona Lotes Pro paso a paso.',
      icon: PlayCircle,
      link: 'https://www.youtube.com/@lotespro-com',
      color: 'bg-red-500',
      tags: ['YouTube', 'Tutoriales', 'Videos'],
      external: true
    },
    {
      title: 'Imagenes de Drone',
      description: 'Sube tus propias imagenes aereas capturadas con drone para visualizarlas como capa base en el mapa.',
      icon: Camera,
      link: '/documentacion/imagenes-drone',
      color: 'bg-cyan-500',
      tags: ['Drone', 'Ortofoto', 'Aereo'],
      comingSoon: true
    },
    {
      title: 'Realidad Virtual',
      description: 'Integra experiencias de realidad virtual para que tus clientes puedan recorrer los lotes de forma inmersiva.',
      icon: Glasses,
      link: '/documentacion/realidad-virtual',
      color: 'bg-indigo-500',
      tags: ['VR', '360', 'Inmersivo'],
      comingSoon: true
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-4">
            <BookOpen className="w-8 h-8 text-orange-600 dark:text-orange-400" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Documentacion
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Guias y recursos para aprovechar al maximo el sistema. Aprende a importar datos,
            convertir formatos y configurar tu proyecto.
          </p>
        </div>

        {/* YouTube Section */}
        <div className="mb-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30">
                <PlayCircle className="w-8 h-8 text-red-500" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Canal de YouTube - Lotes Pro
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Suscribete a nuestro canal de YouTube donde publicamos tutoriales de como funciona Lotes Pro.
                Aprende paso a paso a configurar tu proyecto, importar datos y mucho mas.
              </p>
            </div>
            <div className="flex-shrink-0">
              <a
                href="https://www.youtube.com/@lotespro-com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Ver Canal
              </a>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documentacionItems.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <a
                key={index}
                href={item.comingSoon ? '#' : item.link}
                {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className={`group relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-all duration-200 ${
                  item.comingSoon
                    ? 'opacity-75 cursor-not-allowed'
                    : 'hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-600 hover:-translate-y-1'
                }`}
                onClick={(e) => item.comingSoon && e.preventDefault()}
              >
                {/* Coming Soon Badge */}
                {item.comingSoon && (
                  <div className="absolute top-3 right-3 px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded-full">
                    Proximamente
                  </div>
                )}

                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${item.color} mb-4`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  {item.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {item.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Arrow indicator */}
                {!item.comingSoon && (
                  <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                )}
              </a>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="mt-12 bg-orange-50 dark:bg-orange-900/20 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            ¿Necesitas ayuda adicional?
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Nuestro equipo de soporte esta disponible para ayudarte con cualquier duda.
          </p>
          <a
            href="/contacto"
            className="inline-flex items-center px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors"
          >
            Contactar Soporte
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DocumentacionPage;
